import clsx from "clsx";

export function SkeletonBlock({ className }: { className?: string }) {
  return <div className={clsx("rounded-lg bg-white/[0.07]", className)} />;
}

function SkeletonStatus({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      aria-label="กำลังโหลดหน้า"
      aria-busy="true"
      className="min-w-0 animate-pulse motion-reduce:animate-none"
    >
      <span className="sr-only">กำลังโหลด…</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <SkeletonStatus>
      <div className="mx-auto flex max-w-[1440px] flex-col gap-3">
        <SkeletonBlock className="mb-1 h-7 w-40" />
        <div className="grid gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-[28px] border border-border bg-surface p-4"
            >
              <SkeletonBlock className="h-3 w-28" />
              <SkeletonBlock className="mt-3 h-12 w-3/4" />
              <SkeletonBlock className="mt-3 h-4 w-full" />
            </div>
          ))}
        </div>
        <div className="rounded-[28px] border border-border bg-surface p-4">
          <SkeletonBlock className="h-5 w-40" />
          <div className="my-3 grid grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <SkeletonBlock key={i} className="h-4 w-full" />
            ))}
          </div>
          <SkeletonBlock className="h-44 w-full" />
        </div>
        <div className="grid gap-3 lg:grid-cols-[1.7fr_1fr]">
          <SkeletonBlock className="h-64 rounded-[28px]" />
          <SkeletonBlock className="h-64 rounded-[28px]" />
        </div>
      </div>
    </SkeletonStatus>
  );
}

export function TablePageSkeleton() {
  return (
    <SkeletonStatus>
      <div className="flex min-w-0 flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          <SkeletonBlock className="h-7 w-44" />
          <SkeletonBlock className="h-5 w-28" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <SkeletonBlock className="h-7 w-24" />
            <SkeletonBlock className="h-7 w-24" />
          </div>
          <SkeletonBlock className="h-8 w-64 max-w-full" />
        </div>
        <div className="overflow-hidden rounded-2xl border border-border bg-surface p-4">
          <SkeletonBlock className="mb-5 h-8 w-full" />
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              className="grid grid-cols-4 gap-5 border-t border-border py-4"
            >
              <SkeletonBlock className="h-4 w-3/4" />
              <SkeletonBlock className="h-4 w-full" />
              <SkeletonBlock className="h-4 w-2/3" />
              <SkeletonBlock className="h-4 w-full" />
            </div>
          ))}
        </div>
      </div>
    </SkeletonStatus>
  );
}

export function ChartSkeleton() {
  return (
    <div
      role="status"
      aria-label="กำลังโหลดกราฟ"
      className="h-full w-full animate-pulse motion-reduce:animate-none"
    >
      <span className="sr-only">กำลังโหลดกราฟ…</span>
      <SkeletonBlock className="h-full w-full" />
    </div>
  );
}

export function SectorRrgSkeleton() {
  return <SkeletonStatus><div className="mx-auto flex max-w-[1440px] flex-col gap-4">
    <div className="flex items-center justify-between"><SkeletonBlock className="h-7 w-40" /><SkeletonBlock className="h-7 w-28" /></div>
    <SkeletonBlock className="h-10 w-48 rounded-xl" />
    <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(390px,0.9fr)_minmax(0,1.35fr)]">
      <div className="rounded-2xl border border-border bg-surface p-4"><SkeletonBlock className="mb-3 h-5 w-48" /><div className="mb-3 grid grid-cols-2 gap-2">{[0,1,2,3].map((i)=><SkeletonBlock key={i} className="h-10" />)}</div><SkeletonBlock className="h-[360px]" /></div>
      <div className="rounded-2xl border border-border bg-surface p-4"><SkeletonBlock className="mb-4 h-5 w-36" />{Array.from({length:10},(_,i)=><SkeletonBlock key={i} className="mb-3 h-6 w-full" />)}</div>
    </div>
  </div></SkeletonStatus>;
}

export function MarketBreadthSkeleton() {
  return <SkeletonStatus><div className="mx-auto flex max-w-[1440px] flex-col gap-4">
    <div className="flex items-center justify-between"><SkeletonBlock className="h-7 w-72" /><SkeletonBlock className="h-6 w-36" /></div>
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
      <div className="space-y-3">{Array.from({length:6},(_,i)=><div key={i} className="rounded-2xl border border-border bg-surface p-3"><SkeletonBlock className="mb-2 h-4 w-52" /><SkeletonBlock className="h-24 w-full" /></div>)}</div>
      <div className="space-y-2 rounded-2xl border border-border bg-surface p-4"><SkeletonBlock className="mb-2 h-5 w-40" />{Array.from({length:8},(_,i)=><SkeletonBlock key={i} className="h-12 w-full rounded-xl" />)}</div>
    </div>
  </div></SkeletonStatus>;
}

export function HedgeEngineSkeleton() {
  return <SkeletonStatus><div className="mx-auto flex max-w-[1440px] flex-col gap-4">
    <div className="flex items-center justify-between"><SkeletonBlock className="h-7 w-36" /><SkeletonBlock className="h-6 w-36" /></div>
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.8fr)_minmax(320px,1fr)]">
      <div className="rounded-2xl border border-border bg-surface p-4"><SkeletonBlock className="mb-3 h-5 w-40" />{Array.from({length:4},(_,i)=><SkeletonBlock key={i} className="mb-2 h-[118px] w-full rounded-xl" />)}</div>
      <div className="grid grid-cols-2 content-start gap-2"><SkeletonBlock className="col-span-2 h-36 rounded-2xl" /><SkeletonBlock className="col-span-2 h-28 rounded-2xl" /><SkeletonBlock className="col-span-2 h-32 rounded-2xl" />{Array.from({length:4},(_,i)=><SkeletonBlock key={i} className="h-20 rounded-2xl" />)}</div>
    </div>
  </div></SkeletonStatus>;
}

export function DualRrgSkeleton() {
  return <SkeletonStatus><div className="mx-auto flex max-w-[1440px] flex-col gap-4">
    <div className="flex items-center justify-between"><SkeletonBlock className="h-7 w-32" /><SkeletonBlock className="h-10 w-48 rounded-xl" /></div>
    <div className="overflow-hidden rounded-2xl border border-border bg-surface"><div className="flex justify-between border-b border-border p-4"><SkeletonBlock className="h-5 w-44" /><SkeletonBlock className="h-4 w-24" />
    </div><div className="p-4"><div className="mb-3 grid grid-cols-12 gap-3">{Array.from({length:12},(_,i)=><SkeletonBlock key={i} className="h-3" />)}</div>{Array.from({length:12},(_,row)=><div key={row} className="grid grid-cols-12 gap-3 border-t border-border/50 py-3">{Array.from({length:12},(_,col)=><SkeletonBlock key={col} className="h-4" />)}</div>)}</div></div>
  </div></SkeletonStatus>;
}
